import { client, handleRequest, handleRequestBody } from "./client.ts";
import type { Construction } from "./constructions.ts";

export type ScheduleNoteSeverity = "info" | "warning" | "critical";

export interface ScheduleNote {
	id: string;
	stage_id?: string;
	stage_name?: string;
	severity: ScheduleNoteSeverity;
	body: string;
	resolved_at: string | null;
	created_by_id: string;
	created_by_name: string;
	created_at: string;
	updated_at: string;
}

export interface ListScheduleNotesResponse {
	count: number;
	items: ScheduleNote[];
	construction: Construction;
}

export function listScheduleNotes(
	constructionId: string,
): Promise<ListScheduleNotesResponse> {
	return handleRequestBody(() =>
		client.get<ListScheduleNotesResponse>(
			`/constructions/${constructionId}/schedule-notes`,
		),
	);
}

export interface CreateScheduleNoteInput {
	stageId?: string;
	severity: ScheduleNoteSeverity;
	body: string;
}

export interface UpdateScheduleNoteInput {
	stageId?: string | null;
	severity?: ScheduleNoteSeverity;
	body?: string;
	resolved?: boolean;
}

interface ScheduleNoteResponse {
	note: ScheduleNote;
}

export async function createScheduleNote(
	constructionId: string,
	input: CreateScheduleNoteInput,
): Promise<ScheduleNote> {
	const { note } = await handleRequestBody(() =>
		client.post<ScheduleNoteResponse>(
			`/constructions/${constructionId}/schedule-notes`,
			{
				stage_id: input.stageId ?? null,
				severity: input.severity,
				body: input.body,
			},
		),
	);
	return note;
}

export async function updateScheduleNote(
	constructionId: string,
	noteId: string,
	input: UpdateScheduleNoteInput,
): Promise<ScheduleNote> {
	const { note } = await handleRequestBody(() =>
		client.patch<ScheduleNoteResponse>(
			`/constructions/${constructionId}/schedule-notes/${noteId}`,
			{
				stage_id:
					input.stageId === undefined ? undefined : (input.stageId ?? ""),
				severity: input.severity,
				body: input.body,
				resolved: input.resolved,
			},
		),
	);
	return note;
}

export async function deleteScheduleNote(
	constructionId: string,
	noteId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(`/constructions/${constructionId}/schedule-notes/${noteId}`),
	);
}
